// The words and checks for the Sign-in codes card in Settings, Integrations (spec C1, C14).
// The Settings board has no design for this card yet, so it copies the Carrier fetch card;
// lines marked "proposed" are not on the board.
import type { CodeRules, CodeRulesView, SettingChange } from '../auth/api';

export const integrationsCopy = {
  title: 'Integrations',
  adminGroup: 'Admin',
  // proposed
  cardTitle: 'Sign-in codes',
  // proposed
  cardSub: 'The 6-digit code emailed at every sign-in',
  // proposed
  cardNote:
    'Changes apply to the next code sent. A code already sent keeps the rules it was sent with.',
  cancel: 'Cancel',
  history: (n: number) => `History (${n})`,
  hideHistory: 'Hide history',
  noHistory: 'No changes yet.',
  unsaved: 'Unsaved changes',
  save: 'Save',
  saved: 'Integration settings saved.',
  // proposed
  somethingWrong: 'Something went wrong. Try again.',
} as const;

type Field = { key: keyof CodeRules; label: string; unit?: string };

// proposed: the labels, in the order of spec C1.
export const codeRuleFields: Field[] = [
  { key: 'expiryMinutes', label: 'Code works for', unit: 'minutes' },
  { key: 'resendSeconds', label: 'Resend offered after', unit: 'seconds' },
  { key: 'maxWrongTries', label: 'Wrong tries per code' },
];

/** What the inputs hold: text, so a half-typed or cleared number is kept as typed. */
export type RuleDraft = Record<keyof CodeRules, string>;

export const draftOf = (rules: CodeRules): RuleDraft => ({
  expiryMinutes: String(rules.expiryMinutes),
  resendSeconds: String(rules.resendSeconds),
  maxWrongTries: String(rules.maxWrongTries),
});

/** The error line under a field, or null when it is a whole number inside the bounds. */
export function fieldError(value: string, bounds: { min: number; max: number }): string | null {
  const n = /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN;
  if (Number.isInteger(n) && n >= bounds.min && n <= bounds.max) return null;
  // proposed
  return `Enter a whole number from ${bounds.min} to ${bounds.max}.`;
}

/** The rules to save, or null while any field has an error. */
export function rulesOf(draft: RuleDraft, bounds: CodeRulesView['bounds']): CodeRules | null {
  for (const { key } of codeRuleFields) if (fieldError(draft[key], bounds[key])) return null;
  return {
    expiryMinutes: Number(draft.expiryMinutes.trim()),
    resendSeconds: Number(draft.resendSeconds.trim()),
    maxWrongTries: Number(draft.maxWrongTries.trim()),
  };
}

export const isDirty = (draft: RuleDraft, saved: CodeRules) =>
  codeRuleFields.some(({ key }) => draft[key].trim() !== String(saved[key]));

// proposed: "Defaults: 10 minutes, 30 seconds, 5 tries."
export function defaultsLine(d: CodeRules): string {
  return `Defaults: ${d.expiryMinutes} minutes, ${d.resendSeconds} seconds, ${d.maxWrongTries} tries.`;
}

const stampFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** "Oct 14 11:20" in New York time, as the board's History rows show it. */
export function stamp(iso: string): string {
  const parts = Object.fromEntries(
    stampFormat.formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  );
  return `${parts.month} ${parts.day} ${parts.hour}:${parts.minute}`;
}

/** One History row: when and who, then the field with its old and new value. */
export function historyRow(change: SettingChange) {
  const field = codeRuleFields.find((f) => f.key === change.field);
  return {
    when: `${stamp(change.at)}, ${change.by ?? 'Inflowmate'}`,
    field: field?.label ?? change.field,
    before: String(change.before ?? ''),
    after: String(change.after ?? ''),
  };
}
